import type { StaffCreatedRequest } from "@lafayette/schemas";
import { useState } from "react";

import { CustomerList } from "../../analytics/components/CustomerList";
import { Button } from "../../ui/button";
import { CommandDialog } from "../../ui/command-dialog";
import { Dialog } from "../../ui/dialog";
import { SelectField, TextAreaField, TextField } from "../../ui/fields";
import { describeCreateFailure } from "./errors";
import {
  bodyOf,
  type FieldErrors,
  type FieldKey,
  type FormValues,
  fieldLabels,
  fieldsFor,
  requestTypeOptions,
  validateForm,
} from "./form-model";

// The two windows of "New request" (CHT-2051, ADR 0018). Step one picks the customer from the list that the SERVER allows (the Analytics picker:
// a concierge sees only the customers they cover). Step two is the form, a kit `CommandDialog`: one idempotency key for the dialog, the fields
// locked while a retry is possible, a failure shown inside the dialog and nothing sent before the person confirms.

export type PickedCustomer = Readonly<{ id: string; name: string }>;

export function ChooseCustomerDialog({
  onPick,
  onClose,
}: Readonly<{ onPick: (customer: PickedCustomer) => void; onClose: () => void }>) {
  return (
    <Dialog
      title="New request"
      description="Choose the customer this request is for. You see only the customers you may open."
      onClose={onClose}
    >
      <CustomerList
        onPick={(id, name) => onPick({ id, name })}
        size="dialog"
        idPrefix="new-request-customer"
      />
      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}

const fieldId = (key: string) => `new-request-${key.replace(".", "-")}`;

const INPUT_FIELDS: Readonly<
  Partial<
    Record<
      FieldKey,
      {
        type?: "date" | "time" | "number";
        inputMode?: "numeric";
        hint?: string;
        maxLength?: number;
        autoComplete?: string;
      }
    >
  >
> = {
  dateFrom: { type: "date" },
  dateTo: { type: "date" },
  preferredTime: { type: "time" },
  timeZone: { hint: "The zone of the place, for example Asia/Kolkata.", maxLength: 64 },
  partySize: { type: "number", inputMode: "numeric" },
  locationText: { maxLength: 200 },
  "details.pickupLocation": { maxLength: 200 },
  "details.dropoffLocation": { maxLength: 200 },
};

function TypeFields({
  values,
  errors,
  onChange,
}: Readonly<{
  values: FormValues;
  errors: FieldErrors;
  onChange: (key: keyof FormValues, value: string) => void;
}>) {
  return fieldsFor(values.requestType).map(({ key, required }) => {
    const common = {
      id: fieldId(key),
      label: fieldLabels[key],
      value: values[key],
      onChange: (next: string) => onChange(key, next),
      error: errors[key],
      required,
    };
    if (key === "specialRequests") {
      return <TextAreaField key={key} {...common} rows={3} maxLength={2000} />;
    }
    const extra = INPUT_FIELDS[key] ?? {};
    return <TextField key={key} {...common} {...extra} />;
  });
}

export function RequestFormDialog({
  customer,
  values,
  onValues,
  create,
  onChangeCustomer,
  onDone,
  onClose,
}: Readonly<{
  customer: PickedCustomer;
  values: FormValues;
  onValues: (values: FormValues) => void;
  create: (body: ReturnType<typeof bodyOf>, idempotencyKey: string) => Promise<StaffCreatedRequest>;
  onChangeCustomer: () => void;
  onDone: (created: StaffCreatedRequest) => void;
  onClose: () => void;
}>) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const edit = (key: keyof FormValues, value: string) => {
    onValues({ ...values, [key]: value });
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  return (
    <CommandDialog<StaffCreatedRequest>
      title="New request"
      description="The request is sent to the concierge team at once. Fill in the details the customer would give."
      confirmLabel="Create request"
      pendingLabel="Creating…"
      order="confirm-first"
      clearFailureOn="edit"
      focusFailure
      validate={() => {
        const found = validateForm(values);
        setErrors(found);
        return Object.keys(found).length === 0;
      }}
      mutate={async (idempotencyKey) => {
        try {
          const created = await create(bodyOf(values), idempotencyKey);
          return { ok: true, value: created };
        } catch (cause) {
          return { ok: false, failure: describeCreateFailure(cause) };
        }
      }}
      onDone={onDone}
      onClose={onClose}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="m-0 text-sm leading-6">
          <span className="text-muted-foreground">Customer: </span>
          <span className="font-semibold">{customer.name}</span>
        </p>
        <Button type="button" variant="link" className="px-0" onClick={onChangeCustomer}>
          Change customer
        </Button>
      </div>
      <SelectField
        id="new-request-type"
        label="Type of request"
        value={values.requestType}
        onChange={(next) => edit("requestType", next)}
        options={requestTypeOptions}
        placeholder="Choose a type"
        error={errors.requestType}
        required
      />
      <TextField
        id="new-request-title"
        label="Title"
        value={values.title}
        onChange={(next) => edit("title", next)}
        error={errors.title}
        maxLength={140}
        required
      />
      <TypeFields values={values} errors={errors} onChange={edit} />
    </CommandDialog>
  );
}
